import { useCallback, useEffect, useRef, useState } from 'react';
import { db, CachedTask, CachedShelter, CachedBoardPost } from '../offline/db';
import { api } from '@/services/api';
import { boardOfflineService } from '../offline/board.service';
import { contactCacheService } from '../offline/contact-cache.service';
import { offlineCredentialService } from '../offline/credential.service';
import { memberCacheService } from '../offline/member-cache.service';
import { prefetchesDirectory, purgePrefetchedDirectoryOnFieldDevice } from '../offline/field-prefetch';
import { offlineSupplyRequestService } from '../offline/supply-request.service';
import { offlineMapFeaturesService } from '../offline/map-features.service';
import { taskOfflineService } from '../offline/task.service';
import { networkService } from '@/services/network.service';

interface OfflineCacheState {
  isSyncing: boolean;
  lastSync: Date | null;
  pendingCount: number;
  cachedTaskCount: number;
  cachedShelterCount: number;
  cachedBoardCount: number;
}

/**
 * Hook to manage offline data caching and synchronization
 * Caches critical data for offline access and tracks pending mutations
 */
export function useOfflineCache(tenantId: string | null, incidentId: string | null) {
  const isSyncingRef = useRef(false);
  const [state, setState] = useState<OfflineCacheState>({
    isSyncing: false,
    lastSync: null,
    pendingCount: 0,
    cachedTaskCount: 0,
    cachedShelterCount: 0,
    cachedBoardCount: 0,
  });

  // Get counts from IndexedDB
  const updateCounts = useCallback(async () => {
    try {
      const [pendingCount, taskCount, shelterCount, boardCount] = await Promise.all([
        db.pendingMutations.count(),
        db.cachedTasks.count(),
        db.cachedShelters.count(),
        db.cachedBoardPosts.count(),
      ]);

      setState((prev) => ({
        ...prev,
        pendingCount: pendingCount,
        cachedTaskCount: taskCount,
        cachedShelterCount: shelterCount,
        cachedBoardCount: boardCount,
      }));
    } catch (error) {
      console.error('Failed to get offline cache counts:', error);
    }
  }, []);

  // Cache tasks for offline access
  const cacheTasks = useCallback(async () => {
    if (!tenantId) return;

    try {
      // Delegate to the task offline service so cached rows get is_synced = 1
      // and any local unsynced edits are conflict-preserved. The previous
      // hand-rolled version bulk-added RAW server tasks with no is_synced field,
      // so every cached task counted as pending (the indicator uses `!is_synced`)
      // — that was the phantom "83 tasks pending" — and it deleted all local
      // rows first, wiping unsynced offline edits.
      const result = await taskOfflineService.cacheFromServer(tenantId);
      // Self-heal rows left flagless by the old raw-cache path (is_synced
      // undefined/null) so they stop counting as pending. Genuine local edits
      // use is_synced === 0 and are deliberately left untouched.
      const healed = await db.cachedTasks
        .filter((t) => t.is_synced === undefined || t.is_synced === null)
        .modify({ is_synced: 1 });
      console.log(`[OfflineCache] Cached ${result.cached} tasks (healed ${healed})`);
    } catch (error) {
      console.error('Failed to cache tasks:', error);
    }
  }, [tenantId]);

  // Cache shelters for offline access
  const cacheShelters = useCallback(async () => {
    if (!tenantId) return;

    try {
      const response = await api.get<{ data: CachedShelter[] }>('/shelters', { limit: 100 });

      const shelters = response.data;

      // Clear existing cached shelters for this tenant
      await db.cachedShelters.where('tenant_id').equals(tenantId).delete();

      // Add new shelters
      if (shelters.length > 0) {
        await db.cachedShelters.bulkAdd(shelters);
      }

      console.log(`[OfflineCache] Cached ${shelters.length} shelters`);
    } catch (error) {
      console.error('Failed to cache shelters:', error);
    }
  }, [tenantId]);

  // Cache board posts for offline access (delegates to boardOfflineService)
  const cacheBoardPosts = useCallback(async () => {
    if (!tenantId) return;

    try {
      const result = await boardOfflineService.cacheFromServer(tenantId, incidentId || undefined);
      console.log(`[OfflineCache] Cached ${result.cached} board posts (${result.conflicts} conflicts preserved)`);
    } catch (error) {
      console.error('Failed to cache board posts:', error);
    }
  }, [tenantId, incidentId]);

  // Cache supply requests for offline access
  const cacheSupplyRequests = useCallback(async () => {
    if (!tenantId) return;

    try {
      const response = await api.get<{ data?: any[] } | any[]>('/supply-requests', { limit: 100 });
      const list = Array.isArray(response)
        ? response
        : ((response as { data?: any[] })?.data ?? []);
      const cached = await offlineSupplyRequestService.cacheFromServer(list);
      console.log(`[OfflineCache] Cached ${cached} supply requests`);
    } catch (error) {
      console.error('Failed to cache supply requests:', error);
    }
  }, [tenantId]);

  // Cache map features for offline access (only when an incident is engaged
  // so we cache a meaningful, scoped set rather than every layer at once).
  const cacheMapFeatures = useCallback(async () => {
    if (!tenantId) return;

    try {
      // Match the default visible layers from map-layers.store.ts; this is the
      // same payload the unified map fetches on /map page load, so caching it
      // pre-warms the screen for offline use.
      const layers = [
        'incidents',
        'damage_assessments',
        'supply_requests',
        'deployed_resources',
        'tasks',
        'shelters',
        'critical_infrastructure',
        'road_closures',
        'utility_outages',
        'weather_alerts',
      ].join(',');
      const response = await api.get<{ features?: any[]; data?: { features?: any[] } }>(
        `/map/features?layers=${encodeURIComponent(layers)}${incidentId ? `&incident_id=${incidentId}` : ''}`
      );
      const features = response?.features ?? response?.data?.features ?? [];
      if (features.length > 0) {
        const cached = await offlineMapFeaturesService.cacheFromServer(tenantId, features as any);
        console.log(`[OfflineCache] Cached ${cached} map features`);
      }
    } catch (error) {
      console.error('Failed to cache map features:', error);
    }
  }, [tenantId, incidentId]);

  // Sync all data for offline use
  const syncForOffline = useCallback(async () => {
    if (!tenantId || !networkService.isOnline()) return;
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;

    setState((prev) => ({ ...prev, isSyncing: true }));

    try {
      // First, push any pending mutations and board posts to the server
      const { pendingMutationsSyncService } = await import('../offline/pending-mutations.service');
      await Promise.all([
        pendingMutationsSyncService.syncAll(),
        boardOfflineService.syncAll(tenantId),
      ]);

      // Then cache fresh data from server (including photos for offline).
      // Caches were previously only pre-warmed for Contacts + Damage Assessments
      // because this hook was never invoked anywhere (OFFLINE-001). MainLayout
      // now invokes us on app mount so all critical lists fill on first load.
      //
      // P2-05: a field device (native shell or /field routes) does NOT copy
      // the tenant-wide contact directory and credential roster: no field
      // screen reads them (see offline/field-prefetch.ts), and it drops what
      // earlier builds copied, once.
      const directory = prefetchesDirectory()
        ? [contactCacheService.cacheFromServer(tenantId), offlineCredentialService.cacheFromServer(tenantId)]
        : [purgePrefetchedDirectoryOnFieldDevice().catch(() => false)];
      await Promise.all([
        cacheTasks(),
        cacheShelters(),
        cacheBoardPosts(),
        cacheSupplyRequests(),
        cacheMapFeatures(),
        ...directory,
        memberCacheService.cacheFromServer(tenantId),
      ]);

      setState((prev) => ({
        ...prev,
        isSyncing: false,
        lastSync: new Date(),
      }));

      await updateCounts();
    } catch (error) {
      console.error('Failed to sync for offline:', error);
      setState((prev) => ({ ...prev, isSyncing: false }));
    } finally {
      isSyncingRef.current = false;
    }
  }, [tenantId, cacheTasks, cacheShelters, cacheBoardPosts, cacheSupplyRequests, cacheMapFeatures, updateCounts]);

  // Get cached tasks
  const getCachedTasks = useCallback(async (): Promise<CachedTask[]> => {
    try {
      if (incidentId) {
        return await db.cachedTasks.where('incident_id').equals(incidentId).toArray();
      }
      if (tenantId) {
        return await db.cachedTasks.where('tenant_id').equals(tenantId).toArray();
      }
      return [];
    } catch (error) {
      console.error('Failed to get cached tasks:', error);
      return [];
    }
  }, [tenantId, incidentId]);

  // Get cached shelters (shelters are incident-independent, so no incident_id filtering)
  const getCachedShelters = useCallback(async (): Promise<CachedShelter[]> => {
    try {
      return await db.cachedShelters.toArray();
    } catch (error) {
      console.error('Failed to get cached shelters:', error);
      return [];
    }
  }, []);

  // Get cached board posts (delegates to boardOfflineService)
  const getCachedBoardPosts = useCallback(async (): Promise<CachedBoardPost[]> => {
    try {
      if (tenantId) {
        return await boardOfflineService.getCachedPosts(tenantId);
      }
      return [];
    } catch (error) {
      console.error('Failed to get cached board posts:', error);
      return [];
    }
  }, [tenantId]);

  // Clear all cached data
  const clearCache = useCallback(async () => {
    try {
      await Promise.all([
        db.cachedTasks.clear(),
        db.cachedShelters.clear(),
        db.cachedBoardPosts.clear(),
      ]);
      await updateCounts();
      console.log('[OfflineCache] Cache cleared');
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }, [updateCounts]);

  // Update counts on mount and when online status changes
  useEffect(() => {
    updateCounts();

    const removeListener = networkService.addListener((connected) => {
      if (connected) {
        // Auto-sync when coming back online
        syncForOffline();
      }
    });

    return () => removeListener();
  }, [updateCounts, syncForOffline]);

  // Initial sync when tenant changes
  useEffect(() => {
    if (tenantId && networkService.isOnline()) {
      // Debounce initial sync
      const timer = setTimeout(() => {
        syncForOffline();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [tenantId, syncForOffline]);

  return {
    ...state,
    syncForOffline,
    getCachedTasks,
    getCachedShelters,
    getCachedBoardPosts,
    clearCache,
    updateCounts,
  };
}

export default useOfflineCache;
